import "@/global.css";
import "@/i18n/i18n";

import { Inter_400Regular, Inter_500Medium } from "@expo-google-fonts/inter";
import {
    PlusJakartaSans_600SemiBold,
    PlusJakartaSans_700Bold,
} from "@expo-google-fonts/plus-jakarta-sans";
import { PortalHost } from "@rn-primitives/portal";
import { useFonts } from "expo-font";
import { NavigationBar } from "expo-navigation-bar";
import { Stack, ThemeProvider } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import * as WebBrowser from "expo-web-browser";
import { useColorScheme } from "nativewind";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { Provider } from "react-redux";

import { AlertDialogHost } from "@/components/AlertDialogHost";
import AppToast from "@/components/AppToast";
import { BiometricLockOverlay } from "@/components/BiometricLockOverlay";
import { ForceUpdateScreen } from "@/features/forceUpdate/ForceUpdateScreen";
import { WhatsNewOverlay } from "@/features/whatsNew/WhatsNewOverlay";
import { useWhatsNewOnLaunch } from "@/features/whatsNew/useWhatsNewOnLaunch";
import { I18nProvider } from "@/i18n";
import { getConfig } from "@/getConfig";
import { AuthProvider, useAuth } from "@/lib/auth";
import { isNativeUpgradeRequired } from "@/lib/appCompatibility";
import { setUpgradeRequiredHandler } from "@/lib/auth/upgradeRequiredHandler";
import { PushNotificationsBootstrap } from "@/lib/pushNotifications";
import { NAV_THEME } from "@/lib/theme";
import { store } from "@/store";
import { ThemePreferenceProvider } from "@/theme";
import Constants from "expo-constants";
import { useStackHeader } from "../components/navigation/useStackHeader";

// On web, OAuth opens a popup that redirects back into the app. This must run as
// soon as the redirect page loads (module scope, not after promptAsync). Root
// layout ensures every route — including the OAuth callback — executes it.
// On iOS/Android it is a no-op; native auth is completed by promptAsync.
WebBrowser.maybeCompleteAuthSession();

SplashScreen.preventAutoHideAsync();

/**
 * Get the title of the app based on the environment and the name of the app.
 * @returns The title of the app.
 */
const getAppTitle = (): string | undefined => {
    const env = getConfig("env") || "dev";
    const name = Constants.expoConfig?.web?.name;
    if (env !== "prod" && name) {
        return `${name} (${env.toLocaleUpperCase()})`;
    }
    return name;
};

function SplashScreenController({
    fontsReady,
    compatibilityReady,
    upgradeRequired,
}: {
    fontsReady: boolean;
    compatibilityReady: boolean;
    upgradeRequired: boolean;
}) {
    const { isLoading } = useAuth();
    const { ready, visible, dismiss } = useWhatsNewOnLaunch();

    useEffect(() => {
        if (!fontsReady) {
            return;
        }
        if (upgradeRequired || (compatibilityReady && !isLoading && ready)) {
            SplashScreen.hideAsync();
        }
    }, [fontsReady, compatibilityReady, upgradeRequired, isLoading, ready]);

    if (!visible || upgradeRequired) {
        return null;
    }

    return <WhatsNewOverlay onContinue={dismiss} />;
}

function RootNavigator() {
    const { isAuthenticated } = useAuth();
    const stackHeaderProps = useStackHeader();
    const { t } = useTranslation();

    return (
        <View className="flex-1">
            <Stack
                screenOptions={{
                    ...stackHeaderProps,
                    headerShown: true,
                }}
            >
                <Stack.Protected guard={isAuthenticated}>
                    <Stack.Screen
                        name="(tabs)"
                        options={{ title: t("nav.home"), headerShown: false }}
                    />
                    <Stack.Screen
                        name="leave-request"
                        options={{
                            title: t("screens.leaveRequest"),
                        }}
                    />
                    {CONFIG.featureMedicalRequestsEnabled && (
                        <Stack.Screen
                            name="medical-request"
                            options={{
                                title: t("screens.medicalRequest"),
                            }}
                        />
                    )}
                    <Stack.Screen
                        name="pickup-request"
                        options={{
                            title: t("screens.pickupRequest"),
                        }}
                    />
                    <Stack.Screen
                        name="settings"
                        options={{
                            title: t("nav.settings"),
                        }}
                    />
                    <Stack.Screen
                        name="contact-us"
                        options={{
                            title: t("nav.contactUs"),
                            presentation: "card",
                        }}
                    />
                    <Stack.Screen
                        name="child"
                        options={{
                            presentation: "card",
                            title: t("screens.absences"),
                        }}
                    />
                    <Stack.Screen
                        name="profile/[pipedriveId]"
                        options={{
                            presentation: "card",
                            headerShown: false,
                        }}
                    />
                    <Stack.Screen
                        name="active-sessions"
                        options={{
                            title: t("activeSessions.title"),
                        }}
                    />
                </Stack.Protected>
                <Stack.Protected guard={!isAuthenticated}>
                    <Stack.Screen
                        name="login"
                        options={{ headerShown: false }}
                    />
                </Stack.Protected>
            </Stack>
        </View>
    );
}

export default function RootLayout() {
    const { colorScheme } = useColorScheme();
    const scheme = colorScheme ?? "light";

    const faRegular = require("../../rnvi-fonts/fontawesome6-pro/FontAwesome6Pro-Regular.ttf");
    const faSolid = require("../../rnvi-fonts/fontawesome6-pro/FontAwesome6Pro-Solid.ttf");

    const [fontsLoaded] = useFonts({
        Inter_400Regular,
        Inter_500Medium,
        PlusJakartaSans_600SemiBold,
        PlusJakartaSans_700Bold,
        ...(Platform.OS === "android"
            ? {
                  // RNVI uses fontFileName (without .ttf) on Android, not postScriptName.
                  FontAwesome6_Pro_Regular: faRegular,
                  FontAwesome6_Pro_Solid: faSolid,
              }
            : {
                  "FontAwesome6Pro-Regular": faRegular,
                  "FontAwesome6Pro-Solid": faSolid,
              }),
    });
    const [upgradeRequired, setUpgradeRequired] = useState(false);
    const [compatibilityReady, setCompatibilityReady] = useState(
        Platform.OS === "web",
    );

    useEffect(() => {
        setUpgradeRequiredHandler(() => setUpgradeRequired(true));
        return () => setUpgradeRequiredHandler(null);
    }, []);

    useEffect(() => {
        if (Platform.OS === "web") {
            return;
        }
        let cancelled = false;
        isNativeUpgradeRequired(getConfig("s4pServerUrl")).then((required) => {
            if (cancelled) {
                return;
            }
            if (required) {
                setUpgradeRequired(true);
            }
            setCompatibilityReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (Platform.OS !== "web") {
            return;
        }
        const title = getAppTitle();
        if (title) {
            document.title = title;
        }
    }, []);

    const barStyle = scheme === "dark" ? "light" : "dark";

    return (
        <Provider store={store}>
            <AuthProvider>
                <KeyboardProvider>
                    <I18nProvider>
                        <ThemePreferenceProvider>
                            {!fontsLoaded ? null : (
                                <ThemeProvider value={NAV_THEME[scheme]}>
                                    <GestureHandlerRootView
                                        style={{ flex: 1 }}
                                        className="flex-1 bg-background font-sans"
                                    >
                                        <StatusBar style={barStyle} />
                                        <NavigationBar style={barStyle} />
                                        {upgradeRequired ? (
                                            <ForceUpdateScreen />
                                        ) : (
                                            <RootNavigator />
                                        )}
                                        <PushNotificationsBootstrap />
                                        <SplashScreenController
                                            fontsReady={fontsLoaded}
                                            compatibilityReady={
                                                compatibilityReady
                                            }
                                            upgradeRequired={upgradeRequired}
                                        />
                                        <BiometricLockOverlay />
                                        <PortalHost />
                                        <AppToast />
                                        <AlertDialogHost />
                                    </GestureHandlerRootView>
                                </ThemeProvider>
                            )}
                        </ThemePreferenceProvider>
                    </I18nProvider>
                </KeyboardProvider>
            </AuthProvider>
        </Provider>
    );
}
